import React, { useState, useEffect } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Coffee, Leaf, Award, Heart, ArrowRight } from 'lucide-react';
import ProductDetailModal from '../components/ProductDetailModal';
import IndonesianMap from '../components/IndonesianMap';

const OurCoffee = () => {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${window.location.origin}/api/admin/products?page=1`);
        const data = await response.json();

        if (data && data.data) {
          const formattedProducts = data.data.map(p => {
            const plainDesc = p.description ? p.description.replace(/<[^>]+>/g, '') : '';
            return {
              ...p,
              description: plainDesc,
              htmlDescription: p.description,
              region: 'Indonesia'
            };
          });
          setProducts(formattedProducts);
        }
      } catch (error) {
        console.error("Failed to fetch products", error);
      }
    };

    fetchProducts();
  }, []);

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      mirror: false,
    });
  }, []);

  const handleProductClick = (product) => {
    setSelectedProduct(product);
  };

  const values = [
    {
      icon: <Leaf className="w-12 h-12" />,
      title: "Sustainable Sourcing",
      description: "We work directly with farmers who practice sustainable agriculture, ensuring environmental protection and fair compensation."
    },
    {
      icon: <Award className="w-12 h-12" />,
      title: "Quality First",
      description: "Every bean is carefully selected and roasted to bring out its unique characteristics and deliver exceptional flavor."
    },
    {
      icon: <Heart className="w-12 h-12" />,
      title: "Community Focus",
      description: "Supporting local farming communities and preserving Indonesia's rich coffee heritage is at the heart of what we do."
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[70vh] min-h-[500px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(/images/coffee-beans-generic.jpg)',
            filter: 'brightness(0.6)'
          }}
        />
        <div className="relative z-10 text-center text-white px-4" data-aos="fade-up">
          <h1 className="text-4xl md:text-7xl font-bold mb-6">Roasted to Perfection</h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto">
            A journey through Indonesia's finest coffee regions. Each origin tells a story of tradition,
            terroir, and the dedicated farmers who nurture these exceptional beans.
          </p>
        </div>
      </section>

      {/* NEW SECTION: Our Coffee & Map */}
      <section className="py-20 bg-[#F5F5F0]">
        <div className="max-w-7xl mx-auto px-6 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto mb-16" data-aos="fade-up">
            <h2 className="text-3xl md:text-5xl font-bold mb-6 text-[#31415A] uppercase tracking-wide">Our Coffee</h2>
            <p className="text-xl leading-relaxed text-gray-700">
              Our coffee is sourced from selected regions across Indonesia, each offering a
              distinct character in its land and culture. From the bright and structured profile of
              <span className="font-bold text-[#C9A14F]"> Java Ijen</span>, the clean and balanced notes of
              <span className="font-bold text-[#C9A14F]"> Aceh Gayo</span>, the deep and complex body
              of <span className="font-bold text-[#C9A14F]"> Sulawesi Toraja</span>, the vibrant acidity of
              <span className="font-bold text-[#C9A14F]"> Bali Kintamani</span>, to the bold and earthy
              character of <span className="font-bold text-[#C9A14F]"> Papua Wamena</span>—each origin reflects the richness and diversity of
              Indonesian coffee.
            </p>
          </div>

          <div data-aos="fade-up" data-aos-delay="200">
            <IndonesianMap />
          </div>
        </div>
      </section>

      {/* Coffee Origins Section */}
      <section className="py-20" style={{ backgroundColor: '#31415A' }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-6 lg:px-8">
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-3xl md:text-5xl font-bold mb-4" style={{ color: '#ffffff' }}>Our Origins</h2>
            <p className="text-xl text-white">
              Explore the distinct character of each region
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <div key={product.id} onClick={() => handleProductClick(product)} className="cursor-pointer" data-aos="fade-up" data-aos-delay={index * 100}>
                <Card className="overflow-hidden border-none shadow-lg hover:shadow-xl transition-shadow h-full">
                  <div className="relative h-[250px] overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                    />
                  </div>
                  <CardHeader>
                    <CardTitle className="text-2xl" style={{ color: '#31415A' }}>
                      {product.name}
                    </CardTitle>
                    <CardDescription className="text-base">
                      {product.region}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex flex-col justify-between">
                    <p className="text-gray-700 mb-4 line-clamp-3">{product.description}</p>
                    <div className="flex justify-end mt-auto">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="group"
                        style={{ color: '#31415A' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleProductClick(product);
                        }}
                      >
                        <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>

          <ProductDetailModal
            isOpen={!!selectedProduct}
            onClose={() => setSelectedProduct(null)}
            product={selectedProduct}
          />
        </div>
      </section>


    </div>
  );
};

export default OurCoffee;
